import React, { useEffect, useState } from 'react';
import { Route, Routes, BrowserRouter, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import './App.css';
import { AppHolder, ThemedGlobalStyle, light } from './settings';
import RootLayout from './components/layout/RootLayout';
import {
  AddressSearch,
  Application,
  ApplicationDetails,
  Code,
  DocumentUploader,
  Impact,
  LandingPage,
  MapView,
  PreLodgementQuestionTemplate,
  PreLodgementReport,
  PromptSelect,
  ReportReview,
  ReviewAndLodge,
  SuccessPage,
} from './modules/pages';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { AdminDashboard } from './modules/adminPages';
import { AdminAuthPage } from './modules/adminPages/login';
import { ToastContainer, ToastMessage, ToastWrapper } from './modules/adminPages/login/login.style';
import { LanguageProvider, useLanguage } from './hooks/useLanguageSwitch';
import { IntlProvider } from 'react-intl';
import EnglishContent from './lang/english.json';
import HindiContent from './lang/hindi.json';
import MandarinContent from './lang/mandarin.json';
import VietnameseContent from './lang/vietnamese.json';
import GreekContent from './lang/greek.json';
import PunjabiContent from './lang/punjabi.json';
import { ToastManager } from './components/uielements/toast';
import QuestionComponent from './modules/pages/generateQuestionTemplate/generateQuestionNewFlow';
import { AdminAddressSearch, AdminMapView } from './modules/Mapper';
import MapperAuthPage from './modules/Mapper/login/login';
import { Procedures } from './modules/adminPages/procedures';
import { Users } from './modules/adminPages/users';
import { Pdfs } from './modules/adminPages/pdf';
import { Clusters } from './modules/adminPages/clusters';
import { ClustersFlow } from './modules/adminPages/clustersFlow';
import { ClustersFlowTesting } from './modules/adminPages/clusterFlowTesting';
import { ClusterQuestionsCreation } from './modules/adminPages/clusterQuestionsCreation';

const CustomToast = ({ message }) => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    // Immediate navigation based on role
    const userRole = localStorage.getItem('userRole');
    const targetPath = userRole === 'admin' ? '/admin' : userRole === 'mapper' ? '/mapper' : '/';

    // Set a small delay for the navigation to ensure toast is visible
    const navigationTimer = setTimeout(() => {
      navigate(targetPath, { replace: true });
    }, 100);

    const hideTimer = setTimeout(() => {
      setIsVisible(false);
    }, 3000);

    return () => {
      clearTimeout(navigationTimer);
      clearTimeout(hideTimer);
    };
  }, [navigate]);

  if (!isVisible) return null;

  return (
    <ToastWrapper>
      <ToastContainer>
        <ToastMessage>{message}</ToastMessage>
      </ToastContainer>
    </ToastWrapper>
  );
};

const ProtectedRoute = ({ children, authPage }) => {
  const { session } = useAuth();
  const location = useLocation();
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const userRole = localStorage.getItem('userRole');
  const isLoggedIn = Boolean(session?.token);

  // Check for role-based access admin vs normal user and show toast if access is denied.
  useEffect(() => {
    if (!isLoggedIn) return;

    const path = location.pathname;

    const isAdminRoute = path.startsWith('/admin');
    const isMapperRoute = path.startsWith('/mapper') && !isAdminRoute;

    if (userRole === 'user' && (isAdminRoute || isMapperRoute)) {
      setToastMessage('Access denied. Users cannot access admin or mapper sections.');
      setShowToast(true);
    } else if (userRole === 'admin' && !isAdminRoute) {
      setToastMessage('Access denied. Admins can only access admin routes.');
      setShowToast(true);
    } else if (userRole === 'mapper' && !isMapperRoute) {
      setToastMessage('Access denied. Mappers can only access mapper routes.');
      setShowToast(true);
    }
  }, [location.pathname, userRole, isLoggedIn]);

  if (!isLoggedIn) {
    return authPage;
  }

  return (
    <>
      {children}
      {showToast && <CustomToast message={toastMessage} />}
    </>
  );
};

const UserRoutes = () => (
  <Routes>
    <Route path="/" element={<RootLayout />}>
      {/* <Route index element={<InitialEnquireScreen />} /> */}
      <Route index element={<Application />} />
      <Route path="mapView" element={<MapView />} />
      <Route path="promptSelect" element={<PromptSelect />} />
      <Route path="codes" element={<Code />} />
      <Route path="impact" element={<Impact />} />
      <Route path="reviewAndLodge" element={<ReviewAndLodge />} />
      <Route path="reportReview" element={<ReportReview />} />
      <Route path="documentUpload" element={<DocumentUploader />} />
      <Route path="success" element={<SuccessPage />} />
      <Route path="generateQuestion" element={<QuestionComponent />} />
      <Route path="addressSearch" element={<AddressSearch />} />
      <Route path="application/:id" element={<ApplicationDetails />} />
      <Route path="/preLodgementReport" element={<PreLodgementReport />} />
      <Route path="/preLodgementQaChat" element={<PreLodgementQuestionTemplate />} />
    </Route>
  </Routes>
);

const AdminRoutes = () => (
  <Routes>
    <Route path="/" element={<RootLayout />}>
      <Route index element={<AdminDashboard />} />
      <Route path="/procedures" element={<Procedures />} />
      <Route path="/users" element={<Users />} />
      <Route path="/pdfs" element={<Pdfs />} />
      <Route path="/clusters" element={<Clusters />} />
      <Route path="/clusterFlows" element={<ClustersFlow />} />
      <Route path="/clusterFlow/test" element={<ClustersFlowTesting />} />
      <Route
        path="/cluster/manageQuestions/:clusterId/:questionId"
        element={<ClusterQuestionsCreation />}
      />
    </Route>
  </Routes>
);

const MapperRoutes = () => (
  <Routes>
    <Route path="/" element={<RootLayout />}>
      <Route index element={<AdminAddressSearch />} />
      <Route path="/mapView" element={<AdminMapView />} />
    </Route>
  </Routes>
);

const Router = () => (
  <Routes>
    <Route
      path="/admin/*"
      element={
        <ProtectedRoute authPage={<AdminAuthPage />}>
          <AdminRoutes />
        </ProtectedRoute>
      }
    />

    <Route
      path="/mapper/*"
      element={
        <ProtectedRoute authPage={<MapperAuthPage />}>
          <MapperRoutes />
        </ProtectedRoute>
      }
    />

    <Route
      path="/*"
      element={
        <ProtectedRoute authPage={<LandingPage />}>
          <UserRoutes />
        </ProtectedRoute>
      }
    />

    <Route path="/*" element={<Navigate to="/" replace />} />
  </Routes>
);

const defaultTheme = light;
const queryClient = new QueryClient();

const SecuredApp = () => {
  const lang = useLanguage();

  const PreferredLanguage =
    lang?.selectedLang === 'en-US'
      ? EnglishContent
      : lang?.selectedLang === 'hi-IN'
        ? HindiContent
        : lang?.selectedLang === 'zh-CN'
          ? MandarinContent
          : lang?.selectedLang === 'vi-VN'
            ? VietnameseContent
            : lang?.selectedLang === 'el-GR'
              ? GreekContent
              : lang?.selectedLang === 'pa-IN'
                ? PunjabiContent
                : EnglishContent;

  return (
    <ThemeProvider theme={defaultTheme}>
      <IntlProvider locale={'en-US'} messages={PreferredLanguage}>
        <ThemedGlobalStyle />
        <QueryClientProvider client={queryClient}>
          <AppHolder id="app-global">
            <Router />
          </AppHolder>
        </QueryClientProvider>
        <ToastManager position="top-right" duration={5000} />
      </IntlProvider>
    </ThemeProvider>
  );
};

function App() {
  return (
    <>
      <AuthProvider>
        <BrowserRouter>
          <LanguageProvider>
            <Routes>
              <Route path="/*" element={<SecuredApp />} />
            </Routes>
          </LanguageProvider>
        </BrowserRouter>
      </AuthProvider>
    </>
  );
}

export default App;
